import * as API from '../api';
import {useCallback, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {createByIdApiFactory} from '@di-nxt/resource-container';
import {getConfig} from '../config';
import {
  createEntityReduxFactory,
  createResetAllAction,
  createDataSyncUnsubscribeAll,
} from '@di-nxt/resource-container';
import {AuthError, AuthTokens} from './types';
import {
  generateMockTokenSet,
  getAuthTokensStatus,
  hasTokenExpired,
  hasValidAccessToken,
} from './functions';
import {createRefreshTokensApi} from './createRefreshTokensApi';
import assert from 'assert-ts';
import {registerWithStore} from '../../store/registerWithStore';

const EntityName = 'Auth';
type LoginRequest = API.Components.Schemas.ApiLoginRequest;

type AuthTokensWithId = AuthTokens & {id: number};
const TOKENSET_ID = 0;

const createAuthContainer = () => {
  // Create Redux container for Auth Tokens
  const mode = getConfig().Api.Mode;
  const baseUrl = getConfig().Api.BaseUrl;
  const mockFailureRate = getConfig().Api.Mock.FailureRate;
  const refreshTokenLiftime = getConfig().Api.Mock.RefreshTokenLifetime || 3600;

  // Redux
  const redux = createEntityReduxFactory<AuthTokensWithId>(EntityName, 'id');

  //
  const mockFactory = () =>
    generateMockTokenSet(
      Math.max(10, Math.ceil(refreshTokenLiftime / 2)),
      refreshTokenLiftime,
    );

  // Login Api
  const loginApi = createByIdApiFactory<
    AuthTokens,
    'accessToken',
    {},
    LoginRequest
  >({
    idKey: 'accessToken',
    baseUrl,
    endPoint: '/login/v-1',
    mockData: [],
    mockIdFactory: () => '',
    mockFactory,
    mode,
    mockFailureRate,
  });

  // Refresh tokens Api
  const refreshTokensApi = createRefreshTokensApi({
    baseUrl,
    mode,
    endPoint: '/login/v-1/refresh',
    mockFactory,
    mockFailureRate,
  });

  // Coordinated refresh token Api
  let activeRefreshTokenRequest: Promise<AuthTokens | undefined> | undefined;
  /**
   * Provide onSuccess and onError to have coordinated handling of success and error across "shared" promise
   */
  const coordinatedRefreshTokenApi = (
    refreshToken: string,
    onSuccess: (tokens: AuthTokens) => void,
    onError: (error: Error) => void,
  ) => {
    if (!activeRefreshTokenRequest) {
      activeRefreshTokenRequest = hasTokenExpired(refreshToken)
        ? // refresh token expired => fail, tokens expired
          Promise.reject<AuthTokens>(
            new AuthError(
              'Refresh token has expired',
              'auth.refreshtoken.expired',
            ),
          )
        : refreshTokensApi(refreshToken);

      activeRefreshTokenRequest
        .then(res => {
          activeRefreshTokenRequest = undefined;
          if (onSuccess) onSuccess(assert(res));
          return res;
        })
        .catch(error => {
          activeRefreshTokenRequest = undefined;

          if (onError) {
            onError(error);
          } else {
            throw error;
          }
          return undefined;
        });
    }

    return activeRefreshTokenRequest;
  };

  // Use currnet Auth tokens, if any
  const useCurrentAuthTokens = () =>
    useSelector(redux.selectors.single(TOKENSET_ID)) as AuthTokens | undefined;

  // Authenticate
  const useAuthenticate = () => {
    const dispatch = useDispatch();
    return useCallback(
      (username: string, password: string) => {
        return loginApi.create({username, password}).then(res => {
          // Clear previous tokens
          dispatch(redux.actions.reset());
          // Add next tokens
          dispatch(redux.actions.add([{id: TOKENSET_ID, ...res}]));
          return res;
        });
      },
      [dispatch],
    );
  };

  // Unsubscribe all
  const unsubscribeAll = createDataSyncUnsubscribeAll(getConfig);

  // Logout
  const useLogout = () => {
    const tokens = useCurrentAuthTokens();
    const dispatch = useDispatch();

    return useCallback(() => {
      // Clear tokens and all other state
      dispatch(createResetAllAction());

      if (tokens) {
        unsubscribeAll(tokens.accessToken);
      }
    }, [dispatch, tokens]);
  };

  // Use Auth token refresh
  const useGetTokensWithRefreshOrLogout = () => {
    const dispatch = useDispatch();
    const logout = useLogout();
    const tokens = useCurrentAuthTokens();

    return useCallback(() => {
      // no tokens => fail, no tokens (assumes logout already handled)
      if (tokens === undefined) {
        // console.log(
        //   '****** useGetTokensWithRefresh: no tokens => fail, no tokens',
        // );
        return Promise.reject(
          new AuthError('No tokens. Need to login', 'auth.no.tokens'),
        );
      }

      const {accessToken, refreshToken} = tokens;

      // valid access token => ok
      if (!hasTokenExpired(accessToken)) {
        // console.log('****** useGetTokensWithRefresh: valid access token => ok');
        return Promise.resolve(tokens);
      }

      return coordinatedRefreshTokenApi(
        refreshToken,
        (newTokens: AuthTokens) => {
          console.log(
            'useGetTokensWithRefresh: refresh tokens: successful => ok',
          );
          // Replace tokens
          dispatch(redux.actions.add([{id: TOKENSET_ID, ...newTokens}]));
        },
        (error: Error) => {
          console.log(
            'useGetTokensWithRefresh: Refresh token failed/refresh token expired => logout:',
            error,
          );
          logout();
        },
      );
    }, [dispatch, logout, tokens]);
  };

  // Use Auth tokens, will refresh if neccessary, or logout if refresh failed
  const useAuthToken = () => {
    const tokens = useCurrentAuthTokens();
    const getTokens = useGetTokensWithRefreshOrLogout();
    const tokenStatus = getAuthTokensStatus(tokens);

    useEffect(() => {
      if (tokenStatus !== 'Valid') {
        // console.log(
        //   `****** useAuthToken:useEffect: expired - call getTokensWithRefresh`,
        // );

        getTokens()
          // .then(res => {
          //   console.log(
          //     '****** useAuthToken:useEffect: getTokensWithRefresh success',
          //   );
          //   return res;
          // })
          .catch(error => {
            console.log(
              'useAuthToken:useEffect: getTokensWithRefresh failed - do nothing',
            );
          });
      }
    }, [getTokens, tokenStatus, tokens]);

    // console.log(`****** useAuthToken:`, tokenStatus, JSON.stringify(tokens));

    return hasValidAccessToken(tokens) ? tokens : undefined;
  };

  return {
    useAuthenticate,
    useLogout,
    useCurrentAuthTokens,
    useGetTokensWithRefresh: useGetTokensWithRefreshOrLogout,
    useAuthToken,
    redux,
  };
};

const {
  redux: authRedux,
  useAuthenticate,
  useGetTokensWithRefresh,
  useCurrentAuthTokens,
  useAuthToken,
  useLogout,
} = createAuthContainer();

registerWithStore(authRedux, 'persistent');

export {
  useAuthenticate,
  useGetTokensWithRefresh,
  useCurrentAuthTokens,
  useAuthToken,
  useLogout,
};
